/* Where the API is, and who we are while developing.
 *
 * The app never hard-codes a host. In development Vite proxies `/erpapi` to
 * the local gateway; in production nginx does the same thing on the product
 * address, so the built bundle is identical in both.
 *
 * The identity headers exist only for the development gateway
 * (`scripts/dev_header_identity.py`), which believes them because it is bound
 * to localhost with DEBUG on and demands a shared key as well. In production
 * the resolver ignores them entirely and reads the signed-in token, so
 * shipping this code is not shipping a way in.
 */
import { getToken } from '@jackdesk/shell'

export const API_BASE = import.meta.env.VITE_ERP_BASE ?? '/erpapi'

/** jackdesk_auth — the one sign-in every Jackdesk app shares.
 *
 *  The production fallback is the real origin, never '': an empty base
 *  resolves every call against the page itself, which shipped a broken
 *  sign-in once before (customers-ui). */
export const AUTH_BASE = (
  import.meta.env.VITE_AUTH_BASE
  ?? (import.meta.env.DEV ? '/authapi' : 'https://auth.jackdesk.com')
).replace(/\/$/, '')

export const APP_NAME = 'Jackdesk ERP'

// Normally only in development. The performance rig also needs them, because
// it measures the real production bundle rather than the dev server's
// hundreds of separate modules — and a bundle that cannot reach the gateway
// measures nothing. It is opt-in at build time and never set for a release.
const DEV_ONLY_HEADERS = import.meta.env.DEV || import.meta.env.VITE_DEV_IDENTITY === '1'

export type DevIdentity = { org: string; user: string; role: string }

const KEY = 'erp.devIdentity'
const DEFAULT: DevIdentity = { org: '11', user: 'owner@demo.test', role: 'owner' }

export function readDevIdentity(): DevIdentity {
  try {
    const raw = localStorage.getItem(KEY)
    if (raw) return { ...DEFAULT, ...(JSON.parse(raw) as Partial<DevIdentity>) }
  } catch { /* private window: the default is fine */ }
  return DEFAULT
}

export function writeDevIdentity(next: DevIdentity) {
  try { localStorage.setItem(KEY, JSON.stringify(next)) } catch { /* nothing depends on it sticking */ }
}

export function headers(): Record<string, string> {
  // A real session always wins. In development both can exist — somebody
  // signs in properly on a machine that also has the identity switcher — and
  // sending both would let the switcher silently override who they are.
  const token = getToken()
  if (token) return { Authorization: `Bearer ${token}` }

  if (!DEV_ONLY_HEADERS) return {}
  const who = readDevIdentity()
  return {
    'X-API-Key': import.meta.env.VITE_SERVICE_KEY ?? 'live-proof',
    'X-Jackdesk-Org': who.org,
    'X-Jackdesk-User': who.user,
    'X-Jackdesk-Role': who.role,
  }
}
